// Shared NOAA tide station registry for San Diego.
// Used by /tides (station selector + SEO variants), sitemap generator,
// and beach detail pages that link to the nearest station.

export interface TideStation {
  /** NOAA CO-OPS station id. */
  id: string;
  /** Short display label used in the selector and headings. */
  label: string;
  /** Long-form name used in copy / meta description. */
  longName: string;
  /** Coast area / neighborhood served. */
  area: string;
  /** One-line description of which beaches/breaks this station best represents. */
  covers: string;
  /** Approximate station coordinates (used for sunrise / sunset lookup). */
  lat: number;
  lon: number;
  /** Headline used on the station variant of /tides. */
  heading: string;
  /** Area-specific planning advice shown under the intro. */
  advice: string;
}


// NOAA tide-prediction reference stations inside San Diego County.
// Ordered south → north so the selector and hub read geographically.
// Verified against NOAA's station directory (Oct 2026). Note: Oceanside has
// no NOAA tide-prediction station of its own — La Jolla (9410230) is NOAA's
// official reference for the North County coast.
export const TIDE_STATIONS: TideStation[] = [
  {
    id: "9410120",
    label: "Imperial Beach",
    longName: "Imperial Beach, Pacific Ocean",
    area: "South Bay (open coast)",
    covers:
      "Imperial Beach Pier, the Tijuana Slough mouth, Border Field, and the open-ocean South Bay shoreline — the only true open-coast tide station in the South Bay.",
    lat: 32.578,
    lon: -117.135,
    heading: "Imperial Beach Tide Chart",
    advice:
      "This is the open-coast reference for the southernmost beaches, so the swing is wider than the sheltered bay stations to the east. Imperial Beach surf works through most of the tide, but the sandbars off the pier shape up best on a mid-to-low incoming tide. During big south swells, a high tide can push water to the seawall — check the chart before a beach walk.",
  },
  {
    id: "9410135",
    label: "South San Diego Bay",
    longName: "South San Diego Bay",
    area: "South Bay",
    covers:
      "Chula Vista waterfront, Sweetwater Marsh, the Coronado Cays, and the bay side of Silver Strand. The reference for protected bay paddling and South Bay harbor timing.",
    lat: 32.629,
    lon: -117.108,
    heading: "South San Diego Bay Tide Chart",
    advice:
      "This is a sheltered bay station, so highs and lows are muted compared with the open coast. For kayakers launching from Chula Vista or paddleboarders crossing to Coronado, the bay station is more accurate than Imperial Beach. Plan around a rising tide for easier returns against a weakening ebb.",
  },
  {
    id: "9410152",
    label: "National City",
    longName: "National City, San Diego Bay",
    area: "South Bay / San Diego Bay",
    covers:
      "National City marina, Pepper Park, the 24th Street waterfront, and the mid-bay shipping channels. A subordinate NOAA station referenced to Broadway Pier for the central South Bay shoreline.",
    lat: 32.667,
    lon: -117.107,
    heading: "National City Tide Chart",
    advice:
      "National City sits midway up the bay, so highs and lows arrive later and run smaller than at the harbor entrance. Use it for launching from Pepper Park or paddling the Sweetwater Channel; for ocean beaches like Imperial Beach, the open-coast Imperial Beach station is the better match.",
  },
  {
    id: "9410166",
    label: "Quarantine Station",
    longName: "San Diego, Quarantine Station",
    area: "Point Loma / harbor entrance",
    covers:
      "The Point Loma harbor entrance, Shelter Island's south side, Ballast Point, and the channel into San Diego Bay. A subordinate NOAA station referenced to Broadway Pier at the mouth of the bay.",
    lat: 32.689,
    lon: -117.234,
    heading: "Point Loma Harbor Entrance Tide Chart",
    advice:
      "Quarantine Station sits right at the harbor mouth, so it tracks the open coast more closely than Broadway Pier while still reflecting bay conditions. Boaters transiting the entrance should watch the ebb against a swell, and anglers fishing Ballast Point do best on a moving tide.",
  },
  {
    id: "9410170",
    label: "San Diego (Broadway Pier)",
    longName: "San Diego Bay (Broadway Pier)",
    area: "Point Loma / San Diego Bay",
    covers:
      "Ocean Beach, Sunset Cliffs, Point Loma, Cabrillo tide pools, Coronado, and everything inside San Diego Bay — the standard reference for OB and Sunset Cliffs surf windows.",
    lat: 32.714,
    lon: -117.174,
    heading: "San Diego Bay Tide Chart",
    advice:
      "Because Broadway Pier sits inside the harbor, highs arrive a few minutes later here than on the open coast and the swing is slightly smaller. Sunset Cliffs surfs best through the middle of the tide — too high and it backs off, too low and the reef shows. Cabrillo's tide pools are safest explored on a falling tide, and Coronado's wide flat beach walks best anywhere below about 2 ft.",
  },
  {
    id: "9410196",
    label: "Mission Bay",
    longName: "Mission Bay, Campland",
    area: "Mission Bay",
    covers:
      "Mission Bay, Fiesta Island, Crown Point, Pacific Beach channel, and the entrance jetty. The reference for bay boating, jetty fishing, and Mission Beach surf timing.",
    lat: 32.794,
    lon: -117.224,
    heading: "Mission Bay Tide Chart",
    advice:
      "Mission Bay is a shallow, engineered lagoon, so the tide lags the open coast and the range is smaller. For boaters, plan departures on a rising tide for extra clearance over the flats; for jetty anglers, the outgoing tide pulls bait through the entrance. Mission Beach surf is best matched to this station rather than the open-coast La Jolla gauge.",
  },
  {
    id: "9410230",
    label: "La Jolla",
    longName: "La Jolla (Scripps Pier)",
    area: "North-central coast",
    covers:
      "La Jolla Cove, La Jolla Shores, Windansea, Black's Beach, Del Mar, Torrey Pines, and the whole North County coast through Oceanside and Carlsbad — NOAA's official reference station for North County, and the standard for tide-pooling and reef surf timing.",
    lat: 32.867,
    lon: -117.257,
    heading: "La Jolla & North County Tide Chart",
    advice:
      "La Jolla is the open-ocean reference for the north-central coast. For snorkeling the Cove, a calm morning on a tide near or below 0.0 ft gives the best visibility and the shortest swim over the reef. For Windansea, Black's Beach and the Del Mar reefs, match the swell direction to a mid-to-low tide window; the Shores stays surfable through most of the swing. Minus tides open the reef shelf below Scripps Pier — the widest tide-pool access in the county outside Cabrillo.",
  },
];


export const DEFAULT_TIDE_STATION_ID = "9410230";

export const findTideStation = (id?: string | null): TideStation =>
  TIDE_STATIONS.find((s) => s.id === id) ??
  TIDE_STATIONS.find((s) => s.id === DEFAULT_TIDE_STATION_ID)!;

/** Ordered station ids for sitemap emission. */
export const TIDE_STATION_IDS = TIDE_STATIONS.map((s) => s.id);
